import { RouteContext } from '@common/contexts';
import { runInAction } from 'mobx';
import React, { useContext, useEffect } from 'react';
import { matchRoutes, useResolvedPath } from 'react-router';
import { BreadcrumbsRoute } from 'use-react-router-breadcrumbs';

export type useRouteBreadcrumbResult = {
  setBreadCrumb: (breadCrumb: string | undefined) => void;
  breadCrumb: string | undefined;
};
/**
 * This hook makes it possible to set a routes bread crumb based on where it is in the route element tree.
 * For example <Route element={<SomeContainer />} />  In SomeContainer if it uses useRouteBreadCrumb's setBreadCrumb it will set the bread crumb for the route where SomeContainer is it's element.
 * @returns an object container the setBreadCrumb function and the current breadCrumb text
 */
export const useRouteBreadcrumb = (): useRouteBreadcrumbResult => {
  const [breadCrumb, setBreadCrumb] = React.useState<string | undefined>(
    undefined,
  );
  const [breadCrumbRoute, setBreadCrumbRoute] = React.useState<
    BreadcrumbsRoute | undefined
  >(undefined);
  const routes = useContext(RouteContext.get());
  const pathInfo = useResolvedPath('.');

  //rematch the path anyting the current resolved component path or routes collection changes
  useEffect(() => {
    const matches = matchRoutes(routes, pathInfo.pathname);
    if (!breadCrumbRoute && matches && matches.length > 0)
      setBreadCrumbRoute(matches[matches.length - 1].route as BreadcrumbsRoute); //store the bread crumb route in state
  }, [
    pathInfo,
    pathInfo.pathname,
    breadCrumbRoute,
    setBreadCrumbRoute,
    routes,
  ]);

  //update the breadcrumbs text anything the breadCrumbRoute matched changes
  useEffect(() => {
    if (breadCrumbRoute) {
      //the magic that makes this work is that breadcrumb is observable on the RouteObject, so we have to run in action to update it here.
      runInAction(() => {
        breadCrumbRoute.breadcrumb = breadCrumb ?? 'Loading...';
      });
    }
  }, [breadCrumbRoute, breadCrumb]);

  return {
    setBreadCrumb: setBreadCrumb,
    breadCrumb,
  };
};
